/**
 * ProviderDetails Component
 * Displays detailed provider information similar to booking details page
 */

import { useState, useEffect, useRef, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { Listbox } from '@headlessui/react';
import {
  Star,
  Edit2,
  Save,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Phone,
  Store,
  Globe,
  Calendar,
  PoundSterling,
  BadgeCheck,
  CreditCard,
  FileText,
  Tag,
} from 'lucide-react';
import { formatDate, formatCurrency } from '../utils/helpers';
import { getAvatarBackgroundColor, getUserImageSrc, getUserInitial } from '../utils/userImage';
import { realAPI } from '../api/realEndpoints';
import type { ProviderDetailsData, providerService } from '../api/adminTypes';
import { IMAGE_URL } from '../api/client';
import { useApiErrorHandler } from '../utils/apiErrorHandler';
import { useToastNotifications } from '../hooks/useToast';
import Button from '../components/Button';
import Modal from '../components/Modal';
import '../pages/ContentManagement.css';

interface ProviderDetailsProps {
  providerId: string | number;
  isDeletedView?: boolean;
}

// Separate component for Provider Information (Left Div - Sticky)
function ProviderInfo({
  provider,
  canEdit,
  isEditing,
  isSaving,
  editName,
  editStatus,
  editIsFeatured,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditNameChange,
  onEditStatusChange,
  onEditIsFeaturedChange,
  onOpenImagePreview,
  onSelectServices,
  onSelectReviews,
}: {
  provider: ProviderDetailsData;
  canEdit: boolean;
  isEditing: boolean;
  isSaving: boolean;
  editName: string;
  editStatus: 'active' | 'inactive';
  editIsFeatured: 'yes' | 'no';
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditNameChange: (name: string) => void;
  onEditStatusChange: (status: 'active' | 'inactive') => void;
  onEditIsFeaturedChange: (isFeatured: 'yes' | 'no') => void;
  onOpenImagePreview: (imageSrc: string) => void;
  onSelectServices: () => void;
  onSelectReviews: () => void;
}) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'inactive':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const providerImageSrc = getUserImageSrc(provider.image, provider.signInProvider);
  const displayPhone =
    provider.mobileNumber && provider.mobileNumber.trim() !== ''
      ? (() => {
          const code = provider.countryCode?.trim() || '1';
          const cleanCode = code.startsWith('+') ? code.substring(1) : code;
          return `+${cleanCode}-${provider.mobileNumber}`;
        })()
      : undefined; // or null

  return (
    <div className="w-full">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
            Provider Info
          </h3>
          {canEdit ? (
            isEditing ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={onCancelEdit}
                  className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-70"
                  disabled={isSaving}
                >
                  <span className="inline-flex items-center gap-1">
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </span>
                </button>
                <button
                  onClick={onSaveEdit}
                  className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-70"
                  disabled={isSaving}
                >
                  <span className="inline-flex items-center gap-1">
                    <Save className="w-3.5 h-3.5" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </span>
                </button>
              </div>
            ) : (
              <button
                onClick={onStartEdit}
                className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:from-blue-700 hover:to-indigo-700"
              >
                <Edit2 className="w-3.5 h-3.5" />
                Edit Provider
              </button>
            )
          ) : null}
        </div>

        <div className="mb-6 mt-4 flex flex-col items-center gap-3">
          {providerImageSrc ? (
            <img
              src={providerImageSrc}
              alt={provider.name}
              className="h-32 w-32 cursor-pointer rounded-full border-4 border-blue-100 object-cover dark:border-blue-900/50"
              onClick={() => onOpenImagePreview(providerImageSrc)}
            />
          ) : (
            <div
              className="flex h-32 w-32 items-center justify-center rounded-full text-5xl font-bold text-white"
              style={{ backgroundColor: getAvatarBackgroundColor(provider.name) }}
            >
              {getUserInitial(provider.name, 'P')}
            </div>
          )}

          <div className="text-center">
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => onEditNameChange(e.target.value)}
                  maxLength={30}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-900"
                />
                <p className="text-xs text-gray-500">{editName.length}/30</p>
              </div>
            ) : (
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {provider.name}
              </p>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{provider.email}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={onSelectServices}
              className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-left transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-700/40 dark:hover:bg-gray-700/60"
            >
              <p
                style={{ fontSize: '11px' }}
                className="text-[10px] font-semibold uppercase tracking-wide text-black-500 dark:text-gray-400"
              >
                Services
              </p>
              <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                {provider.totalServices || provider.providerServices?.length || 0}
              </p>
            </button>
            <button
              type="button"
              onClick={onSelectReviews}
              className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-left transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-700/40 dark:hover:bg-gray-700/60"
            >
              <p
                style={{ fontSize: '11px' }}
                className="text-[10px] font-semibold uppercase tracking-wide text-black-500 dark:text-gray-400"
              >
                Reviews
              </p>
              <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                {provider.totalRating || provider.providerRatings?.length || 0}
              </p>
            </button>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 dark:border-gray-700 dark:bg-gray-700/40">
              <p
                style={{ fontSize: '11px' }}
                className="text-[10px] font-semibold uppercase tracking-wide text-black-500 dark:text-gray-400"
              >
                Avg Rating
              </p>
              <p className="flex items-center gap-1 text-base font-bold text-gray-900 dark:text-gray-100">
                <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                {provider.averageRating?.toFixed(1) || '0.0'}
              </p>
            </div>
          </div>

          {displayPhone && (
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                <Phone className="h-4 w-4 text-indigo-500" />
                Mobile
              </label>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{displayPhone}</p>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              {getStatusIcon(isEditing ? editStatus : provider.status)}
              Status
            </label>
            {isEditing ? (
              <div className="relative w-24">
                <Listbox value={editStatus} onChange={onEditStatusChange}>
                  {/* Button */}
                  <Listbox.Button
                    className={`w-full h-8 px-3 pr-6 text-sm font-medium text-left border rounded-md shadow-sm focus:outline-none focus:ring-1 transition-colors
          ${
            editStatus === 'active'
              ? 'border-green-400 text-green-600 focus:ring-green-400'
              : 'border-red-400 text-red-600 focus:ring-red-400'
          }`}
                  >
                    <span className="block truncate capitalize">{editStatus}</span>
                    <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                  </Listbox.Button>

                  {/* Options */}
                  <Listbox.Options className="absolute z-20 mt-1 w-full bg-white rounded-md shadow-md border overflow-hidden">
                    <Listbox.Option
                      value="active"
                      className={({ active, selected }) =>
                        `px-3 py-1.5 cursor-pointer transition-colors text-sm
              ${
                selected
                  ? 'bg-green-100 text-green-700 font-semibold'
                  : active
                    ? 'bg-green-50 text-green-600'
                    : 'text-gray-700'
              }`
                      }
                    >
                      Active
                    </Listbox.Option>

                    <Listbox.Option
                      value="inactive"
                      className={({ active, selected }) =>
                        `px-3 py-1.5 cursor-pointer transition-colors text-sm
              ${
                selected
                  ? 'bg-red-100 text-red-700 font-semibold'
                  : active
                    ? 'bg-red-50 text-red-600'
                    : 'text-gray-700'
              }`
                      }
                    >
                      Inactive
                    </Listbox.Option>
                  </Listbox.Options>
                </Listbox>
              </div>
            ) : (
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${
                  provider.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {provider.status || 'inactive'}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <img
                src={
                  (isEditing ? editIsFeatured : provider.isFeatured) === 'yes'
                    ? '/featured.svg'
                    : '/9784192.svg'
                }
                alt="Featured status"
                className="h-4 w-4 object-contain"
              />
              Featured
            </label>

            {isEditing ? (
              <div className="relative w-16">
                <Listbox value={editIsFeatured} onChange={onEditIsFeaturedChange}>
                  {/* Button */}
                  <Listbox.Button
                    className={`w-full h-8 px-2 pr-6 text-sm font-medium text-left border rounded-md shadow-sm focus:outline-none focus:ring-1 transition-colors
          ${
            editIsFeatured === 'yes'
              ? 'border-green-400 text-green-600 focus:ring-green-400'
              : 'border-red-400 text-red-600 focus:ring-red-400'
          }`}
                  >
                    <span className="block truncate capitalize">{editIsFeatured}</span>

                    <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                  </Listbox.Button>

                  {/* Options */}
                  <Listbox.Options className="absolute z-20 mt-1 w-full bg-white rounded-md shadow-md border overflow-hidden">
                    <Listbox.Option
                      value="yes"
                      className={({ active, selected }) =>
                        `px-2 py-1.5 cursor-pointer transition-colors text-sm
              ${
                selected
                  ? 'bg-green-100 text-green-700 font-semibold'
                  : active
                    ? 'bg-green-50 text-green-600'
                    : 'text-gray-700'
              }`
                      }
                    >
                      Yes
                    </Listbox.Option>

                    <Listbox.Option
                      value="no"
                      className={({ active, selected }) =>
                        `px-2 py-1.5 cursor-pointer transition-colors text-sm
              ${
                selected
                  ? 'bg-red-100 text-red-700 font-semibold'
                  : active
                    ? 'bg-red-50 text-red-600'
                    : 'text-gray-700'
              }`
                      }
                    >
                      No
                    </Listbox.Option>
                  </Listbox.Options>
                </Listbox>
              </div>
            ) : (
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${
                  provider.isFeatured === 'yes'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {provider.isFeatured || 'no'}
              </span>
            )}
          </div>

          {provider?.servicesOffered && (
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                {provider.servicesOffered.toLowerCase() === 'store' && (
                  <Store className="h-4 w-4 text-blue-500" />
                )}
                {provider.servicesOffered.toLowerCase() === 'mobile' && (
                  <Globe className="h-4 w-4 text-blue-500" />
                )}
                Work Mode
              </label>

              <p className="text-sm font-medium capitalize text-gray-900 dark:text-gray-100">
                {provider.servicesOffered}
              </p>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
              <div className="h-4 w-4 text-purple-500">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" />
                </svg>
              </div>
              Registered On
            </label>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {provider.createdAt ? formatDate(provider.createdAt) : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderAddress({ location }: { location?: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {/* Title with Icon */}
      <div className="mb-2 flex items-center gap-2">
        <MapPin className="h-5 w-5 text-red-500" />
        <p className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
          Address
        </p>
      </div>

      {location ? (
        <p className="break-words whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
          {location}
        </p>
      ) : (
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
          Location not added yet
        </p>
      )}
    </div>
  );
}

// Separate component for Services and Reviews (Right Div)
function ProviderServicesReviews({
  allServices,
  provider,
  servicesPage,
  setServicesPage,
  onServicesPageChange,
  onReviewsPageChange,
  isReviewsLoading,
  plansData,
  isPlansLoading,
  onPlanClick,
  activeTab,
  setActiveTab,
  tabSectionRef,
}: {
  allServices: providerService[];
  provider: ProviderDetailsData;
  servicesPage: number;
  setServicesPage: (page: number) => void;
  onServicesPageChange?: (page: number) => void;
  onReviewsPageChange?: (page: number) => void;
  isReviewsLoading?: boolean;
  plansData: any;
  isPlansLoading?: boolean;
  onPlanClick: (id: string | number) => void;
  activeTab: 'services' | 'reviews' | 'subscriptions';
  setActiveTab: (tab: 'services' | 'reviews' | 'subscriptions') => void;
  tabSectionRef: RefObject<HTMLDivElement | null>;
}) {
  const navigate = useNavigate();
  const [reviewsPage, setReviewsPage] = useState(1);
  const [previewGallery, setPreviewGallery] = useState<{
    images: string[];
    index: number;
    title: string;
  } | null>(null);
  const itemsPerPage = 6;

  const toMediaUrl = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) return trimmed;
    if (!IMAGE_URL) return trimmed;
    const base = IMAGE_URL.endsWith('/') ? IMAGE_URL.slice(0, -1) : IMAGE_URL;
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${base}${path}`;
  };

  const getServiceImages = (serviceImage: providerService['serviceImage']): string[] => {
    if (!serviceImage) return [];

    if (typeof serviceImage === 'string') {
      const url = toMediaUrl(serviceImage);
      return url ? [url] : [];
    }

    if (Array.isArray(serviceImage)) {
      return serviceImage
        .map((item) => {
          if (typeof item === 'string') return toMediaUrl(item);
          if (item && typeof item === 'object' && 'url' in item && typeof item.url === 'string') {
            return toMediaUrl(item.url);
          }
          return '';
        })
        .filter(Boolean);
    }

    return [];
  };

  return (
    <div ref={tabSectionRef} className="xl:col-span-3 flex">
      <div className="w-full min-h-[70vh] rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {/* Tabs */}
        <div className="mb-4 flex gap-2 border-b border-gray-200 pb-2 dark:border-gray-600">
          <button
            onClick={() => setActiveTab('services')}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === 'services'
                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Services ({allServices?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === 'reviews'
                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Reviews ({provider.totalRating || provider.providerRatings?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === 'subscriptions'
                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Subscriptions
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'services' && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Services</h3>

            {allServices?.length > 0 ? (
              <>
                <div className="space-y-2">
                  {allServices.map((service) => {
                    const serviceImages = getServiceImages(service.serviceImage);
                    const serviceImageSrc = serviceImages.length > 0 ? serviceImages[0] : null;
                    return (
                      <div
                        key={service.id}
                        className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-700/40 sm:flex sm:gap-3"
                      >
                        {serviceImageSrc ? (
                          <div className="flex flex-col items-center gap-1">
                            <img
                              src={serviceImageSrc}
                              className="h-24 w-full rounded object-cover transition-opacity hover:opacity-80 sm:h-16 sm:w-20"
                              alt={service.serviceName}
                              onClick={() =>
                                setPreviewGallery({
                                  images: serviceImages,
                                  index: 0,
                                  title: service.serviceName || 'Service',
                                })
                              }
                            />
                          </div>
                        ) : (
                          <div className="h-24 w-full rounded bg-gray-200 sm:h-20 sm:w-20" />
                        )}

                        <div className="flex-1">
                          <p className="text-sm font-semibold">{service.serviceName}</p>
                          <p className="text-sm text-black">
                            <strong>Price:</strong>{' '}
                            {formatCurrency(parseFloat(service.price.replace(/[^\d.]/g, '')) || 0)}
                          </p>
                          <p className="text-sm text-black flex items-center gap-1">
                            <strong>Service Time</strong> <Clock className="w-3 h-3" />{' '}
                            {service.duration || 0} Minutes
                          </p>
                        </div>
                        <div className="mt-2 flex justify-start sm:mt-0 sm:justify-end">
                          <div
                            className="
    inline-flex items-center justify-center
    bg-blue-100 text-blue-700
    font-semibold text-center
    whitespace-nowrap
    rounded-[5px]
    px-2
    py-[2px]  /* very small vertical padding */
    text-sm
  "
                            style={{
                              display: 'flex',
                              height: '26px',
                              borderRadius: '10px',
                            }}
                          >
                            {service.categoryName}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Services Pagination */}
                {(provider.totalServices || 0) > itemsPerPage && (
                  <div className="mt-6 flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Showing {(servicesPage - 1) * itemsPerPage + 1} to{' '}
                      {Math.min(servicesPage * itemsPerPage, provider.totalServices || 0)} of{' '}
                      {provider.totalServices || 0} services
                    </p>
                    <div className="flex items-center gap-4">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const newPage = servicesPage - 1;
                          setServicesPage(newPage);
                          onServicesPageChange?.(newPage);
                        }}
                        disabled={servicesPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        Page {servicesPage} of{' '}
                        {Math.ceil((provider.totalServices || 0) / itemsPerPage)}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const newPage = servicesPage + 1;
                          setServicesPage(newPage);
                          onServicesPageChange?.(newPage);
                        }}
                        disabled={servicesPage * itemsPerPage >= (provider.totalServices || 0)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-6">
                <img src="/NoRecord.svg" alt="No services" className="h-72 w-56 object-contain" />
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Customer Reviews
            </h3>

            {(provider.totalRating || provider.providerRatings?.length || 0) > 0 ? (
              <>
                <div className="space-y-3">
                  {isReviewsLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="loader"></div>
                    </div>
                  ) : (
                    provider.providerRatings?.map((rating) => {
                      const reviewerImageSrc = getUserImageSrc(
                        rating.userInfo?.image,
                        rating.userInfo?.signInProvider
                      );

                      return (
                        <div
                          key={rating.id}
                          className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-700/40"
                        >
                          <div className="flex items-start gap-3">
                            {reviewerImageSrc ? (
                              <img
                                src={reviewerImageSrc}
                                className="w-10 h-10 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                                alt={rating.userInfo?.name?.charAt(0)}
                                onClick={() => {
                                  const customerId = rating.userInfo?.id;
                                  if (customerId) {
                                    navigate(`/customers/${customerId}`);
                                  }
                                }}
                              />
                            ) : (
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                                style={{
                                  backgroundColor: getAvatarBackgroundColor(rating.userInfo?.name),
                                }}
                                onClick={() => {
                                  const customerId = rating.userInfo?.id;
                                  if (customerId) {
                                    navigate(`/customers/${customerId}`);
                                  }
                                }}
                              >
                                <span className="text-white font-semibold">
                                  {getUserInitial(rating.userInfo?.name, 'U')}
                                </span>
                              </div>
                            )}

                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p
                                    className="text-sm font-medium cursor-pointer hover:text-blue-600 transition-colors"
                                    onClick={() => {
                                      const customerId = rating.userInfo?.id;
                                      if (customerId) {
                                        navigate(`/customers/${customerId}`);
                                      }
                                    }}
                                  >
                                    {rating.userInfo?.name}
                                  </p>

                                  <div className="flex">
                                    {[...Array(5)].map((_, i) => {
                                      const ratingValue = rating.rating;
                                      if (i < Math.floor(ratingValue)) {
                                        // Full star
                                        return (
                                          <Star
                                            key={i}
                                            className="w-4 h-4 text-yellow-500 fill-current"
                                          />
                                        );
                                      } else if (
                                        i === Math.floor(ratingValue) &&
                                        ratingValue % 1 !== 0
                                      ) {
                                        // Half star
                                        return (
                                          <div key={i} className="relative w-4 h-4">
                                            <Star className="w-4 h-4 text-gray-300 fill-current absolute" />
                                            <div className="absolute overflow-hidden w-1/2 h-full">
                                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                            </div>
                                          </div>
                                        );
                                      } else {
                                        // Empty star
                                        return (
                                          <Star
                                            key={i}
                                            className="w-4 h-4 text-gray-300 fill-current"
                                          />
                                        );
                                      }
                                    })}
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500">
                                  {formatDate(rating.createdAt)}
                                </p>
                              </div>

                              {rating.review && (
                                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                                  {rating.review}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Reviews Pagination */}
                {(() => {
                  const totalRatingCount =
                    provider.totalRating || provider.providerRatings?.length || 0;
                  return totalRatingCount > itemsPerPage;
                })() && (
                  <div className="mt-6 flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Showing {(reviewsPage - 1) * itemsPerPage + 1} to{' '}
                      {Math.min(
                        reviewsPage * itemsPerPage,
                        provider.totalRating || provider.providerRatings?.length || 0
                      )}{' '}
                      of {provider.totalRating || provider.providerRatings?.length || 0} reviews
                    </p>
                    <div className="flex items-center gap-4">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const newPage = reviewsPage - 1;
                          setReviewsPage(newPage);
                          onReviewsPageChange?.(newPage);
                        }}
                        disabled={reviewsPage === 1 || isReviewsLoading}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        Page {reviewsPage} of{' '}
                        {Math.ceil(
                          (provider.totalRating || provider.providerRatings?.length || 0) /
                            itemsPerPage
                        )}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          const newPage = reviewsPage + 1;
                          setReviewsPage(newPage);
                          onReviewsPageChange?.(newPage);
                        }}
                        disabled={
                          reviewsPage * itemsPerPage >=
                            (provider.totalRating || provider.providerRatings?.length || 0) ||
                          isReviewsLoading
                        }
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-6">
                <img src="/NoRecord.svg" alt="No reviews" className="h-72 w-56 object-contain" />
              </div>
            )}
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Subscriptions</h3>
              <div className="text-sm text-gray-500">
                Total: {plansData?.counts ?? plansData?.results?.length ?? 0}
              </div>
            </div>

            {isPlansLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="loader" />
              </div>
            ) : !plansData?.results?.length ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-gray-600">
                This provider currently has no subscriptions.
              </div>
            ) : (
              <div className="space-y-3">
                {plansData.results.map((plan: any) => (
                  <button
                    key={plan.id}
                    onClick={() => onPlanClick(plan.id)}
                    className="w-full text-left grid grid-cols-[1.6fr_0.8fr_1fr_1fr_1fr_0.7fr] gap-3 items-center rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:border-blue-200 hover:shadow-lg transition-all dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="min-w-0">
                      <p className="text-base font-semibold text-gray-900 dark:text-white truncate">
                        {plan.plans?.planName || '—'}
                      </p>
                    </div>
                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      £{Number(plan.plans?.price || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {plan.startDate ? formatDate(plan.startDate) : '-'}
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {plan.endDate ? formatDate(plan.endDate) : '-'}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${
                          plan.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {plan.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        Expired: {plan.isExpired === 'yes' ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 text-right">
                      {plan.paymentGateway?.toUpperCase() || '-'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewGallery && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setPreviewGallery(null)}
        >
          <div
            className="relative w-full max-w-5xl p-6 flex flex-col items-center gap-6"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Image */}
            <img
              src={previewGallery.images[previewGallery.index]}
              alt={previewGallery.title}
              className="w-full max-h-[75vh] object-contain rounded-xl bg-white shadow-2xl"
            />

            {/* Navigation Controls */}
            {previewGallery.images.length > 1 && (
              <div className="flex items-center gap-4 bg-white/95 backdrop-blur-md rounded-xl px-6 py-3 shadow-lg">
                {/* Previous Button */}
                <button
                  type="button"
                  className="p-3 rounded-full border border-blue-200 
                       bg-blue-100 shadow-md
                       hover:bg-blue-200 hover:scale-105 
                       active:scale-95
                       transition-all duration-200"
                  onClick={() =>
                    setPreviewGallery((prev) =>
                      prev
                        ? {
                            ...prev,
                            index: prev.index === 0 ? prev.images.length - 1 : prev.index - 1,
                          }
                        : prev
                    )
                  }
                >
                  <ChevronLeft className="w-6 h-6 text-blue-600" />
                </button>

                {/* Counter */}
                <span className="text-sm font-semibold text-gray-700">
                  {previewGallery.index + 1} / {previewGallery.images.length}
                </span>

                {/* Next Button */}
                <button
                  type="button"
                  className="p-3 rounded-full border border-blue-200 
                       bg-blue-100 shadow-md
                       hover:bg-blue-200 hover:scale-105 
                       active:scale-95
                       transition-all duration-200"
                  onClick={() =>
                    setPreviewGallery((prev) =>
                      prev
                        ? {
                            ...prev,
                            index: prev.index === prev.images.length - 1 ? 0 : prev.index + 1,
                          }
                        : prev
                    )
                  }
                >
                  <ChevronRight className="w-6 h-6 text-blue-600" />
                </button>
              </div>
            )}

            {/* Close Button */}
            <button
              className="absolute top-12 right-12 
                   bg-blue-100 border border-blue-200 
                   rounded-full p-3 shadow-md
                   hover:bg-blue-200 hover:rotate-90 
                   transition-all duration-300"
              onClick={() => setPreviewGallery(null)}
            >
              <XCircle className="w-7 h-7 text-blue-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProviderDetails({
  providerId,
  isDeletedView = false,
}: ProviderDetailsProps) {
  const queryClient = useQueryClient();
  const itemsPerPage = 6;
  const { handleError } = useApiErrorHandler();
  const { success, error: showError } = useToastNotifications();
  const navigate = useNavigate();
  const tabSectionRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'services' | 'reviews' | 'subscriptions'>('services');
  const [servicesPage, setServicesPage] = useState(1);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [selectedPlanId, setSelectedPlanId] = useState<string | number | null>(null);
  const [selectedPlanDetail, setSelectedPlanDetail] = useState<any>(null);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [providerImagePreview, setProviderImagePreview] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    name: string;
    status: 'active' | 'inactive';
    isFeatured: 'yes' | 'no';
  }>({
    name: '',
    status: 'active',
    isFeatured: 'no',
  });

  // Query for Services and Provider Info
  const {
    data: servicesData,
    isLoading: isServicesInitialLoading,
    error: servicesError,
    refetch: refetchServices,
  } = useQuery({
    queryKey: ['provider', providerId, 'services', servicesPage],
    queryFn: () =>
      realAPI.providers.getProviderDetails(
        providerId,
        itemsPerPage,
        (servicesPage - 1) * itemsPerPage
      ),
    placeholderData: keepPreviousData,
  });

  // Query for Reviews
  const {
    data: reviewsData,
    isLoading: isReviewsLoading,
    error: reviewsError,
    refetch: refetchReviews,
  } = useQuery({
    queryKey: ['provider', providerId, 'reviews', reviewsPage],
    queryFn: () =>
      realAPI.providers.getProviderDetails(
        providerId,
        itemsPerPage,
        (reviewsPage - 1) * itemsPerPage
      ),
    placeholderData: keepPreviousData,
  });

  const {
    data: plansData,
    isLoading: isPlansLoading,
    error: plansError,
    refetch: refetchPlans,
  } = useQuery({
    queryKey: ['provider', providerId, 'plans'],
    queryFn: () => realAPI.providerPlans.getProviderPlans(providerId),
    enabled: activeTab === 'subscriptions',
    staleTime: 60_000,
  });

  // Handle errors (monitoring only, UI handled by conditional rendering)
  useEffect(() => {
    if (servicesError) {
      handleError(servicesError);
      console.error('Error fetching provider details:', servicesError);
    }
  }, [servicesError, handleError]);

  if (reviewsError) {
    console.error('Error fetching reviews:', reviewsError);
  }
  if (plansError) {
    console.error('Error fetching plans:', plansError);
  }

  const handleOpenPlan = async (planId: string | number) => {
    setPlanModalOpen(true);
    setIsPlanLoading(true);
    setSelectedPlanDetail(null);
    setSelectedPlanId(planId);
    try {
      const detail = await realAPI.providerPlans.getProviderPlanDetails(planId);
      setSelectedPlanDetail(detail?.results || detail);
    } catch (err) {
      showError('Failed to load plan details', 'Please try again');
      console.error(err);
      setPlanModalOpen(false);
    } finally {
      setIsPlanLoading(false);
    }
  };

  const provider = servicesData?.result || null;
  const allServices = servicesData?.result?.providerServices || [];
  const isDeletedProfile =
    isDeletedView ||
    (provider as any)?.isDeleted === true ||
    (provider as any)?.isDeleted === 'yes' ||
    (provider as any)?.isDeleted === 1;

  // Combine provider info with reviews from the reviews query
  const providerWithReviews = provider
    ? {
        ...provider,
        providerRatings: reviewsData?.result?.providerRatings || [],
      }
    : null;

  const isLoading = isServicesInitialLoading && !provider;
  const error = servicesError
    ? servicesError instanceof Error
      ? servicesError.message
      : 'An error occurred'
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="loader mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(isDeletedView ? '/providers?view=deleted' : '/providers')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors dark:text-gray-400 dark:hover:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Providers
          </button>
        </div>
        <div className="bg-white rounded-lg p-6 dark:bg-gray-800">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-600" />
            <p className="text-red-600">{error || 'Provider not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const handleStartEdit = () => {
    if (isDeletedProfile) return;
    setEditForm({
      name: provider.name || '',
      status: provider.status === 'inactive' ? 'inactive' : 'active',
      isFeatured: provider.isFeatured === 'yes' ? 'yes' : 'no',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditForm({
      name: provider.name || '',
      status: provider.status === 'inactive' ? 'inactive' : 'active',
      isFeatured: provider.isFeatured === 'yes' ? 'yes' : 'no',
    });
    setIsEditing(false);
  };
  const handleSaveEdit = () => {
    if (isDeletedProfile) return;
    const trimmedName = editForm.name.trim();
    if (!trimmedName) {
      showError('Provider name is required', 'Please enter a valid provider name');
      return;
    }

    if (trimmedName.length > 30) {
      showError('Provider name is too long', 'Name must be 30 characters or fewer');
      return;
    }

    const updatedFields = {
      name: trimmedName,
      status: editForm.status,
      isFeatured: editForm.isFeatured,
    };

    // Show popup immediately on save click for responsive UX
    success('Provider updated', 'Changes saved successfully');
    setIsSaving(true);

    // Optimistic local update for instant UI feedback
    queryClient.setQueryData(['provider', providerId, 'services', servicesPage], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        result: {
          ...oldData.result,
          ...updatedFields,
        },
      };
    });

    queryClient.setQueryData(['provider', providerId, 'reviews', reviewsPage], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        result: {
          ...oldData.result,
          ...updatedFields,
        },
      };
    });

    setIsEditing(false);
    setIsSaving(false);

    realAPI.users
      .updateUser(providerId, updatedFields)
      .then(() => {})
      .catch(async (err: any) => {
        showError('Failed to update provider', err?.message || 'Please try again');
        await Promise.all([refetchServices(), refetchReviews()]);
      });
  };

  const providerData = providerWithReviews || provider;

  const handleSummaryCardClick = (tab: 'services' | 'reviews' | 'subscriptions') => {
    setActiveTab(tab);
    requestAnimationFrame(() => {
      tabSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <button
          onClick={() => navigate(isDeletedView ? '/providers?view=deleted' : '/providers')}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Providers
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="xl:col-span-1 self-start space-y-4">
          <ProviderInfo
            provider={providerWithReviews || provider!}
            canEdit={!isDeletedProfile}
            isEditing={isEditing}
            isSaving={isSaving}
            editName={editForm.name}
            editStatus={editForm.status}
            editIsFeatured={editForm.isFeatured}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onSaveEdit={handleSaveEdit}
            onEditNameChange={(name) => setEditForm((prev) => ({ ...prev, name }))}
            onEditStatusChange={(status) => setEditForm((prev) => ({ ...prev, status }))}
            onEditIsFeaturedChange={(isFeatured) =>
              setEditForm((prev) => ({ ...prev, isFeatured }))
            }
            onOpenImagePreview={setProviderImagePreview}
            onSelectServices={() => handleSummaryCardClick('services')}
            onSelectReviews={() => handleSummaryCardClick('reviews')}
          />
          <ProviderAddress location={providerData.location} />
        </div>
        <div className="xl:col-span-3">
          <ProviderServicesReviews
            allServices={allServices}
            provider={providerWithReviews || provider!}
            servicesPage={servicesPage}
            setServicesPage={setServicesPage}
            onServicesPageChange={setServicesPage}
            onReviewsPageChange={setReviewsPage}
            isReviewsLoading={isReviewsLoading}
            plansData={plansData}
            isPlansLoading={isPlansLoading}
            onPlanClick={handleOpenPlan}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabSectionRef={tabSectionRef}
          />
        </div>
      </div>
      <Modal
        isOpen={!!providerImagePreview}
        onClose={() => setProviderImagePreview(null)}
        title="Image Preview"
        size="lg"
      >
        {providerImagePreview ? (
          <img
            src={providerImagePreview}
            alt={providerWithReviews?.name || provider?.name}
            className="w-full max-h-[70vh] object-contain rounded-lg"
          />
        ) : null}
      </Modal>

      <Modal
        isOpen={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        title="Subscription Details"
        size="2xl"
        align="right"
        panelClassName="min-h-[70vh] translate-y-[-50px] w-[calc(100%+5px)] max-w-[calc(theme(width.6xl)+5px)]"
      >
        {isPlanLoading || !selectedPlanDetail ? (
          <div className="flex items-center justify-center py-12">
            <div className="loader" />
          </div>
        ) : (
          <div className="ml-auto w-[95%] max-h-[85vh] overflow-y-auto pr-2">
          {/* Top Section */}
          <div className="grid grid-cols-2 gap-x-10 gap-y-6">
            {/* LEFT COLUMN */}
            <div className="space-y-5">
              {/* Plan */}
              <div className="flex items-center gap-3">
                <Tag className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">PLAN</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {selectedPlanDetail?.plans?.planName || '—'}
                  </p>
                </div>
              </div>

              {/* Start Date */}
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs text-gray-500">START DATE</p>
                  <p>
                    {selectedPlanDetail?.startDate ? formatDate(selectedPlanDetail.startDate) : '-'}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <BadgeCheck className="w-4 h-4 text-green-500" />
                <div>
                  <p className="text-xs text-gray-500">STATUS</p>

                  <span
                    className={`px-2 py-0.5 text-xs font-semibold ${
                      selectedPlanDetail?.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {selectedPlanDetail?.status === 'active'
                      ? 'Active'
                      : selectedPlanDetail?.status === 'inactive'
                        ? 'Inactive'
                        : '-'}
                  </span>
                </div>
              </div>

              {/* Payment */}
              <div className="flex items-center gap-3">
                <CreditCard className="w-4 h-4 text-purple-500" />
                <div>
                  <p className="text-xs text-gray-500">PAYMENT GATEWAY</p>
                  <p className="capitalize">{selectedPlanDetail?.paymentGateway || '-'}</p>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="space-y-5">
              {/* Price */}
              <div className="flex items-center gap-3">
                <PoundSterling className="w-4 h-4 text-green-500" />
                <div>
                  <p className="text-xs text-gray-500">PRICE</p>
                  <p className="font-semibold">
                    £{Number(selectedPlanDetail?.plans?.price || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* End Date */}
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-red-400" />
                <div>
                  <p className="text-xs text-gray-500">END DATE</p>
                  <p>
                    {selectedPlanDetail?.endDate ? formatDate(selectedPlanDetail.endDate) : '-'}
                  </p>
                </div>
              </div>

              {/* Expired */}
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-yellow-500" />
                <div>
                  <p className="text-xs text-gray-500">EXPIRED</p>
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold ${
                      selectedPlanDetail?.isExpired === 'yes'
                        ? 'bg-red-100 text-red-600'
                        : 'bg-green-100 text-green-600'
                    }`}
                  >
                    {selectedPlanDetail?.isExpired === 'yes' ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>

              {/* Created */}
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">CREATED AT</p>
                  <p>
                    {selectedPlanDetail?.createdAt ? formatDate(selectedPlanDetail.createdAt) : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="my-6 border-t border-gray-200 dark:border-gray-700" />

          {/* Description */}
          <div className="flex gap-3">
            <FileText className="w-4 h-4 text-gray-400 mt-1" />
            <div className="w-full">
              <p className="text-xs text-gray-500 mb-2">PLAN DESCRIPTION</p>
              <div className="bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 rounded-lg p-3 text-sm">
                {selectedPlanDetail?.plans?.description || '-'}
              </div>
            </div>
          </div>
        </div>
        )}
      </Modal>
    </div>
  );
}
